import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { Bug, X } from "lucide-react";
import { IconButton } from "@/components/ui/icon-button";
import { useAiDebugMode } from "./use-ai-debug-mode";

/** Reserves workspace space for review, including above the results table. */
export function AiReviewDock({ open, onClose, onContainerChange }: {
  open: boolean;
  onClose: () => void;
  onContainerChange: (element: HTMLDivElement | null) => void;
}) {
  const [width, setWidth] = useState(384);
  const aside = useRef<HTMLElement>(null);
  const drag = useRef<{ x: number; width: number } | null>(null);
  const bodyId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const debug = useAiDebugMode();
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => heading.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);
  function resize(next: number) {
    const parentWidth = aside.current?.parentElement?.clientWidth ?? 1200;
    setWidth(Math.max(320, Math.min(600, parentWidth - 360, next)));
  }
  if (!open) return null;
  return <aside ref={aside} aria-label="AI takeoff review" className="relative flex h-[45%] min-h-48 w-full shrink-0 flex-col border-t bg-background @min-[60rem]:h-full @min-[60rem]:w-[var(--ai-review-width)] @min-[60rem]:border-s @min-[60rem]:border-t-0" style={{ "--ai-review-width": `${width}px` } as CSSProperties}>
    <div role="separator" tabIndex={0} aria-label="Resize AI review pane" aria-orientation="vertical" aria-controls={bodyId} aria-valuemin={320} aria-valuemax={600} aria-valuenow={width}
      className="absolute inset-y-0 -start-1 z-20 hidden w-2 cursor-col-resize touch-none hover:bg-primary/15 focus-visible:outline-2 focus-visible:outline-primary @min-[60rem]:block"
      onKeyDown={event => {
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") { event.preventDefault(); resize(width + (event.key === "ArrowLeft" ? 24 : -24)); }
        if (event.key === "Home" || event.key === "End") { event.preventDefault(); resize(event.key === "Home" ? 320 : 600); }
      }}
      onPointerDown={event => { event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId); drag.current = { x: event.clientX, width }; }}
      onPointerMove={event => { if (drag.current) resize(drag.current.width + drag.current.x - event.clientX); }}
      onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }} />
    <div className="flex min-h-12 items-center justify-between gap-3 border-b px-4">
      <h2 ref={heading} tabIndex={-1} className="text-sm font-semibold focus-visible:outline-2 focus-visible:outline-primary">AI Review</h2>
      <div className="flex items-center gap-1">
        {debug.available && <IconButton icon={Bug} label="AI debug mode" size="sm" pressed={debug.enabled} onClick={() => debug.setEnabled(!debug.enabled)} />}
        <IconButton icon={X} label="Close AI review pane" size="sm" onClick={onClose} />
      </div>
    </div>
    <div id={bodyId} ref={onContainerChange} className="min-h-0 flex-1 overflow-auto overscroll-contain" />
  </aside>;
}
